import React, { memo, useRef, useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useInView, useMotionValue, useSpring, useTransform } from 'framer-motion';
import {
  ArrowRight, ArrowUpRight, Confetti, Champagne, MusicNotes, Users,
  Ticket, MapPin, Camera, Gift, Clock, Headset, Sparkle, Rocket, Heart,
  Lightning, Eye, Megaphone, HandWaving, Smiley
} from '@phosphor-icons/react';
import GalagoLogo from '../components/GalagoLogo';

/* ═══════════════ ANIMATED SCENE — SVG GALAGO INTRO ═══════════════ */

/* Detailed Galago SVG - realistic bush baby with big eyes, sunglasses, cocktail */
function GalagoSVG({ className = '' }) {
  return (
    <svg viewBox="0 0 260 300" className={className} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="furGrad" cx="50%" cy="40%" r="55%">
          <stop offset="0%" stopColor="#B8A07A" />
          <stop offset="70%" stopColor="#8B7355" />
          <stop offset="100%" stopColor="#6B5540" />
        </radialGradient>
        <radialGradient id="bellyGrad" cx="50%" cy="45%" r="50%">
          <stop offset="0%" stopColor="#DDD0B8" />
          <stop offset="100%" stopColor="#C4AD8A" />
        </radialGradient>
        <radialGradient id="earInner" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#E8B898" />
          <stop offset="100%" stopColor="#D4956B" />
        </radialGradient>
        <radialGradient id="eyeGlow" cx="40%" cy="35%" r="50%">
          <stop offset="0%" stopColor="#FFE066" />
          <stop offset="100%" stopColor="#D4A017" />
        </radialGradient>
        <filter id="furShadow" x="-10%" y="-10%" width="120%" height="120%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="2" />
          <feOffset dx="0" dy="3" result="shadow" />
          <feFlood floodColor="#000" floodOpacity="0.3" />
          <feComposite in2="shadow" operator="in" />
          <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {/* Tail - fluffy, curled */}
      <path d="M 175 215 C 200 220 230 200 235 175 C 240 155 225 140 210 145 C 200 148 205 160 210 165" fill="none" stroke="url(#furGrad)" strokeWidth="10" strokeLinecap="round" />
      <path d="M 210 165 C 215 170 220 165 218 158" fill="none" stroke="#9A8465" strokeWidth="6" strokeLinecap="round" />

      {/* Body */}
      <ellipse cx="130" cy="200" rx="48" ry="58" fill="url(#furGrad)" filter="url(#furShadow)" />
      {/* Belly */}
      <ellipse cx="130" cy="210" rx="32" ry="38" fill="url(#bellyGrad)" />
      {/* Fur texture lines on body */}
      <path d="M 95 180 Q 100 185 95 192" fill="none" stroke="#7A6348" strokeWidth="0.8" opacity="0.4" />
      <path d="M 165 178 Q 160 184 165 190" fill="none" stroke="#7A6348" strokeWidth="0.8" opacity="0.4" />
      <path d="M 108 170 Q 112 176 107 182" fill="none" stroke="#7A6348" strokeWidth="0.8" opacity="0.3" />
      <path d="M 152 172 Q 148 178 153 184" fill="none" stroke="#7A6348" strokeWidth="0.8" opacity="0.3" />

      {/* Head - large round */}
      <circle cx="130" cy="120" r="52" fill="url(#furGrad)" filter="url(#furShadow)" />
      {/* Face patch - lighter */}
      <ellipse cx="130" cy="128" rx="36" ry="30" fill="url(#bellyGrad)" />
      {/* Forehead fur tuft */}
      <path d="M 118 72 Q 130 65 142 72" fill="#8B7355" stroke="none" />
      <path d="M 122 74 Q 130 68 138 74" fill="#9A8465" stroke="none" />

      {/* Left Ear - large, detailed */}
      <ellipse cx="82" cy="90" rx="22" ry="32" fill="#B8A07A" transform="rotate(-12,82,90)" />
      <ellipse cx="82" cy="90" rx="16" ry="24" fill="url(#earInner)" transform="rotate(-12,82,90)" />
      <path d="M 72 78 Q 82 72 92 78" fill="none" stroke="#C4956B" strokeWidth="0.5" opacity="0.5" />
      <path d="M 74 85 Q 82 80 90 85" fill="none" stroke="#C4956B" strokeWidth="0.5" opacity="0.4" />
      {/* Right Ear */}
      <ellipse cx="178" cy="90" rx="22" ry="32" fill="#B8A07A" transform="rotate(12,178,90)" />
      <ellipse cx="178" cy="90" rx="16" ry="24" fill="url(#earInner)" transform="rotate(12,178,90)" />
      <path d="M 168 78 Q 178 72 188 78" fill="none" stroke="#C4956B" strokeWidth="0.5" opacity="0.5" />
      <path d="M 170 85 Q 178 80 186 85" fill="none" stroke="#C4956B" strokeWidth="0.5" opacity="0.4" />

      {/* Eyes - HUGE (signature galago feature) */}
      <circle cx="112" cy="115" r="16" fill="#1a1a1a" />
      <circle cx="148" cy="115" r="16" fill="#1a1a1a" />
      <circle cx="112" cy="115" r="13" fill="url(#eyeGlow)" />
      <circle cx="148" cy="115" r="13" fill="url(#eyeGlow)" />
      <circle cx="112" cy="115" r="8" fill="#2a2a2a" />
      <circle cx="148" cy="115" r="8" fill="#2a2a2a" />
      {/* Eye highlights */}
      <circle cx="108" cy="111" r="4" fill="white" opacity="0.85" />
      <circle cx="144" cy="111" r="4" fill="white" opacity="0.85" />
      <circle cx="116" cy="119" r="2" fill="white" opacity="0.4" />
      <circle cx="152" cy="119" r="2" fill="white" opacity="0.4" />

      {/* Sunglasses - sleek, reflective */}
      <path d="M 92 108 Q 92 98 108 98 L 120 98 Q 130 98 130 108 L 130 118 Q 130 126 120 126 L 108 126 Q 92 126 92 118 Z" fill="#1a1a1a" opacity="0.9" />
      <path d="M 130 108 Q 130 98 142 98 L 156 98 Q 168 98 168 108 L 168 118 Q 168 126 156 126 L 142 126 Q 130 126 130 118 Z" fill="#1a1a1a" opacity="0.9" />
      {/* Bridge */}
      <path d="M 125 110 Q 130 106 135 110" fill="none" stroke="#2a2a2a" strokeWidth="3" />
      {/* Temples */}
      <line x1="92" y1="108" x2="72" y2="98" stroke="#2a2a2a" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="168" y1="108" x2="188" y2="98" stroke="#2a2a2a" strokeWidth="2.5" strokeLinecap="round" />
      {/* Lens reflection */}
      <path d="M 98 103 Q 105 100 110 103" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M 138 103 Q 145 100 152 103" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" strokeLinecap="round" />

      {/* Nose */}
      <ellipse cx="130" cy="134" rx="5" ry="3.5" fill="#5C4636" />
      <circle cx="128" cy="133" r="1" fill="#7A6348" opacity="0.5" />

      {/* Mouth - content smile */}
      <path d="M 120 140 Q 130 148 140 140" fill="none" stroke="#6B5544" strokeWidth="1.8" strokeLinecap="round" />

      {/* Left arm + hand holding cocktail */}
      <path d="M 88 185 C 65 190 45 178 38 160" fill="none" stroke="#8B7355" strokeWidth="14" strokeLinecap="round" />
      <circle cx="38" cy="158" r="8" fill="#A08968" />
      {/* Fingers */}
      <circle cx="32" cy="154" r="3" fill="#B8A07A" />
      <circle cx="37" cy="150" r="3" fill="#B8A07A" />
      <circle cx="43" cy="152" r="3" fill="#B8A07A" />

      {/* Cocktail glass - detailed */}
      <g transform="translate(28, 105) rotate(-8)">
        <path d="M -10 30 L 0 0 L 10 30 Z" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.35)" strokeWidth="1" />
        <rect x="-4" y="30" width="8" height="4" rx="1" fill="rgba(255,255,255,0.25)" />
        <rect x="-8" y="34" width="16" height="2.5" rx="1.5" fill="rgba(255,255,255,0.2)" />
        {/* Liquid */}
        <path d="M -7 20 L 0 4 L 7 20 Z" fill="#FFB347" opacity="0.6" />
        {/* Ice cubes */}
        <rect x="-3" y="10" width="4" height="4" rx="1" fill="rgba(255,255,255,0.2)" transform="rotate(15,-1,12)" />
        <rect x="1" y="14" width="3" height="3" rx="1" fill="rgba(255,255,255,0.15)" transform="rotate(-10,2,15)" />
        {/* Straw */}
        <line x1="3" y1="4" x2="12" y2="-18" stroke="#FF6B6B" strokeWidth="2" strokeLinecap="round" />
        {/* Umbrella */}
        <g transform="translate(12,-18)">
          <path d="M -8 0 Q 0 -12 8 0" fill="#FF6B6B" />
          <path d="M -6 -1 Q 0 -10 6 -1" fill="#E85555" />
          <line x1="0" y1="-9" x2="0" y2="4" stroke="#6B3520" strokeWidth="1" />
        </g>
        {/* Orange slice */}
        <circle cx="-4" cy="6" r="4" fill="#FFA500" opacity="0.5" />
        <path d="M -7 6 Q -4 3 -1 6" fill="none" stroke="#FF8C00" strokeWidth="0.5" opacity="0.5" />
      </g>

      {/* Right arm - relaxed */}
      <path d="M 172 185 C 195 178 205 165 200 150" fill="none" stroke="#8B7355" strokeWidth="14" strokeLinecap="round" />
      <circle cx="200" cy="148" r="7" fill="#A08968" />
      {/* Little wave fingers */}
      <circle cx="205" cy="143" r="2.5" fill="#B8A07A" />
      <circle cx="200" cy="141" r="2.5" fill="#B8A07A" />

      {/* Feet - furry toes */}
      <ellipse cx="110" cy="256" rx="16" ry="9" fill="#8B7355" />
      <ellipse cx="150" cy="256" rx="16" ry="9" fill="#8B7355" />
      <circle cx="100" cy="255" r="3.5" fill="#A08968" />
      <circle cx="107" cy="258" r="3" fill="#A08968" />
      <circle cx="114" cy="258" r="3" fill="#A08968" />
      <circle cx="144" cy="258" r="3" fill="#A08968" />
      <circle cx="151" cy="258" r="3" fill="#A08968" />
      <circle cx="158" cy="255" r="3.5" fill="#A08968" />

      {/* Whisker marks */}
      <line x1="100" y1="130" x2="85" y2="128" stroke="#9A8465" strokeWidth="0.6" opacity="0.3" />
      <line x1="100" y1="134" x2="84" y2="136" stroke="#9A8465" strokeWidth="0.6" opacity="0.3" />
      <line x1="160" y1="130" x2="175" y2="128" stroke="#9A8465" strokeWidth="0.6" opacity="0.3" />
      <line x1="160" y1="134" x2="176" y2="136" stroke="#9A8465" strokeWidth="0.6" opacity="0.3" />
    </svg>
  );
}

/* Realistic hammock with plaid pattern */
function HammockSVG({ children }) {
  return (
    <div className="relative">
      <svg viewBox="0 0 560 140" className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-[360px] sm:w-[480px]" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="ropeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#C4A06A" />
            <stop offset="100%" stopColor="#8B7355" />
          </linearGradient>
          <pattern id="plaid" patternUnits="userSpaceOnUse" width="16" height="16">
            <rect width="16" height="16" fill="#D4943A" />
            <rect x="0" y="0" width="8" height="8" fill="#3B7DD8" opacity="0.35" />
            <rect x="8" y="8" width="8" height="8" fill="#3B7DD8" opacity="0.35" />
            <line x1="0" y1="4" x2="16" y2="4" stroke="#D44" strokeWidth="0.8" opacity="0.3" />
            <line x1="4" y1="0" x2="4" y2="16" stroke="#D44" strokeWidth="0.8" opacity="0.3" />
            <line x1="0" y1="12" x2="16" y2="12" stroke="#E8C040" strokeWidth="0.6" opacity="0.3" />
            <line x1="12" y1="0" x2="12" y2="16" stroke="#E8C040" strokeWidth="0.6" opacity="0.3" />
          </pattern>
        </defs>
        {/* Left ropes */}
        <path d="M 50 0 Q 70 20 100 50" fill="none" stroke="url(#ropeGrad)" strokeWidth="3.5" />
        <path d="M 55 0 Q 80 18 108 48" fill="none" stroke="url(#ropeGrad)" strokeWidth="2" opacity="0.5" />
        <path d="M 45 0 Q 65 22 95 52" fill="none" stroke="url(#ropeGrad)" strokeWidth="2" opacity="0.5" />
        {/* Right ropes */}
        <path d="M 510 0 Q 490 20 460 50" fill="none" stroke="url(#ropeGrad)" strokeWidth="3.5" />
        <path d="M 505 0 Q 480 18 452 48" fill="none" stroke="url(#ropeGrad)" strokeWidth="2" opacity="0.5" />
        <path d="M 515 0 Q 495 22 465 52" fill="none" stroke="url(#ropeGrad)" strokeWidth="2" opacity="0.5" />
        {/* Hammock body - plaid fabric */}
        <path d="M 95 50 Q 280 130 465 50" fill="url(#plaid)" stroke="#B8862D" strokeWidth="2" opacity="0.8" />
        {/* Fabric edge */}
        <path d="M 95 50 Q 280 130 465 50" fill="none" stroke="#E8A644" strokeWidth="3" />
        {/* Fringes */}
        {[0,1,2,3,4,5,6,7,8,9,10,11,12].map(i => {
          const t = i / 12;
          const x = 95 + t * 370;
          const y = 50 + Math.sin(t * Math.PI) * 80;
          return <line key={i} x1={x} y1={y} x2={x + (i%2===0?2:-2)} y2={y+10} stroke="#C4A06A" strokeWidth="1.5" opacity="0.4" />;
        })}
      </svg>
      {children}
    </div>
  );
}

/* Realistic string lights with glow */
function StringLights({ count = 12, y = 60 }) {
  return (
    <g>
      {/* Wire */}
      <path d={`M 0 ${y-5} ${Array.from({length: count}).map((_, i) => {
        const x = (i / (count - 1)) * 520;
        const sag = 12 * Math.sin((i / (count - 1)) * Math.PI);
        return `Q ${x} ${y + sag} ${x + 20} ${y + sag - 2}`;
      }).join(' ')}`} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1.2" />

      {count > 0 && Array.from({ length: count }).map((_, i) => {
        const x = (i / (count - 1)) * 500 + 10;
        const ly = y + 10 * Math.sin((i / (count - 1)) * Math.PI);
        const colors = ['#FF6B6B', '#FFD93D', '#6C63FF', '#00C9A7', '#FF8E53'];
        const c = colors[i % colors.length];
        return (
          <g key={i}>
            {/* Glow */}
            <circle cx={x} cy={ly} r="12" fill={c} opacity={0.06}>
              <animate attributeName="opacity" values="0.03;0.1;0.03" dur={`${2.5 + i * 0.3}s`} repeatCount="indefinite" />
            </circle>
            {/* Bulb shape */}
            <ellipse cx={x} cy={ly} rx="3.5" ry="4.5" fill={c} opacity={0.75}>
              <animate attributeName="opacity" values="0.5;0.85;0.5" dur={`${2.5 + i * 0.3}s`} repeatCount="indefinite" />
            </ellipse>
            {/* Highlight */}
            <circle cx={x - 1} cy={ly - 1.5} r="1" fill="white" opacity="0.4" />
          </g>
        );
      })}
    </g>
  );
}

/* Realistic bunting triangles with depth */
function Buntings({ y = 40 }) {
  const colors = ['#FF6B6B', '#FFD93D', '#6C63FF', '#00C9A7', '#FF8E53', '#A78BFA', '#F472B6'];
  return (
    <g>
      <path d={`M -10 ${y} Q 260 ${y-8} 530 ${y}`} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1.5" />
      {Array.from({ length: 20 }).map((_, i) => {
        const t = i / 19;
        const x = t * 520 + 5;
        const sag = -6 * Math.sin(t * Math.PI);
        const c = colors[i % colors.length];
        return (
          <g key={i}>
            <polygon points={`${x-7},${y+sag} ${x+7},${y+sag} ${x},${y+sag+18}`} fill={c} opacity={0.3} />
            <polygon points={`${x-6},${y+sag+1} ${x+6},${y+sag+1} ${x},${y+sag+16}`} fill={c} opacity={0.15} />
          </g>
        );
      })}
    </g>
  );
}

/* Realistic people silhouettes */
function PeopleSilhouettes() {
  const people = [
    { x: 35, s: 1, g: 'M' }, { x: 60, s: 0.9, g: 'F' }, { x: 85, s: 0.85, g: 'M' },
    { x: 120, s: 0.75, g: 'F' }, { x: 170, s: 0.7, g: 'M' },
    { x: 340, s: 0.72, g: 'F' }, { x: 380, s: 0.88, g: 'M' },
    { x: 420, s: 0.95, g: 'F' }, { x: 450, s: 1, g: 'M' }, { x: 480, s: 0.82, g: 'F' },
  ];
  return (
    <g>
      {people.map((p, i) => {
        const h = 50 * p.s;
        const baseY = 275;
        const opacity = 0.12 + (p.s - 0.7) * 0.15;
        return p.g === 'M' ? (
          <g key={i} opacity={opacity}>
            {/* Head */}
            <circle cx={p.x} cy={baseY - h - 8} r={5 * p.s} fill="white" />
            {/* Torso */}
            <path d={`M ${p.x} ${baseY - h - 3} L ${p.x - 8 * p.s} ${baseY - h + 25} L ${p.x + 8 * p.s} ${baseY - h + 25} Z`} fill="white" />
            {/* Legs */}
            <line x1={p.x - 3} y1={baseY - h + 25} x2={p.x - 5} y2={baseY} stroke="white" strokeWidth={3 * p.s} strokeLinecap="round" />
            <line x1={p.x + 3} y1={baseY - h + 25} x2={p.x + 5} y2={baseY} stroke="white" strokeWidth={3 * p.s} strokeLinecap="round" />
          </g>
        ) : (
          <g key={i} opacity={opacity}>
            {/* Head */}
            <circle cx={p.x} cy={baseY - h - 6} r={4.5 * p.s} fill="white" />
            {/* Hair bump */}
            <ellipse cx={p.x} cy={baseY - h - 9} rx={5 * p.s} ry={3 * p.s} fill="white" />
            {/* Dress/body */}
            <path d={`M ${p.x} ${baseY - h - 1} Q ${p.x - 10 * p.s} ${baseY - h + 20} ${p.x - 8 * p.s} ${baseY} L ${p.x + 8 * p.s} ${baseY} Q ${p.x + 10 * p.s} ${baseY - h + 20} ${p.x} ${baseY - h - 1}`} fill="white" />
          </g>
        );
      })}
    </g>
  );
}

/* Full animated scene background */
function AnimatedScene({ phase }) {
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Sky gradient */}
      <div className="absolute inset-0 transition-all duration-[2000ms]" style={{
        background: phase < 2
          ? 'linear-gradient(180deg, #060610 0%, #0d0d20 40%, #100d18 100%)'
          : 'linear-gradient(180deg, #0d1520 0%, #142830 25%, #1a3525 55%, #162218 75%, #0d1a12 100%)'
      }} />

      {/* Stars */}
      {phase < 2 && (
        <div className="absolute inset-0">
          {Array.from({ length: 60 }).map((_, i) => (
            <motion.div key={i}
              className="absolute rounded-full bg-white"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 50}%`,
                width: `${1 + Math.random() * 2}px`,
                height: `${1 + Math.random() * 2}px`,
              }}
              animate={{ opacity: [0.15, 0.7, 0.15] }}
              transition={{ duration: 1.5 + Math.random() * 3, repeat: Infinity, delay: Math.random() * 2 }}
            />
          ))}
          {/* Moon */}
          <div className="absolute top-[12%] right-[18%] w-10 h-10 rounded-full bg-[#E8E0C8] opacity-20 shadow-[0_0_30px_rgba(232,224,200,0.15)]" />
        </div>
      )}

      {/* Event scene SVG */}
      <motion.div
        className="absolute inset-0 flex items-end justify-center"
        animate={{ opacity: phase >= 2 ? 1 : 0 }}
        transition={{ duration: 1.8 }}
      >
        <svg viewBox="0 0 520 310" className="w-full max-h-full" preserveAspectRatio="xMidYMax slice" xmlns="http://www.w3.org/2000/svg">
          {/* Ground with grass texture */}
          <rect x="0" y="275" width="520" height="35" fill="#0d1a10" />
          <ellipse cx="260" cy="278" rx="300" ry="10" fill="#132218" />
          {/* Grass tufts */}
          {Array.from({length: 30}).map((_, i) => (
            <path key={i} d={`M ${i*18} 278 Q ${i*18+3} 270 ${i*18+6} 278`} fill="none" stroke="#1a3018" strokeWidth="1.5" opacity="0.4" />
          ))}

          {/* Trees - left cluster */}
          <circle cx="15" cy="210" r="40" fill="#0d1a0d" opacity="0.6" />
          <circle cx="35" cy="195" r="35" fill="#0f200f" opacity="0.5" />
          <circle cx="5" cy="225" r="30" fill="#0a150a" opacity="0.4" />
          <rect x="20" y="240" width="8" height="38" fill="#0d150d" opacity="0.4" rx="3" />

          {/* Trees - right cluster */}
          <circle cx="505" cy="215" r="38" fill="#0d1a0d" opacity="0.6" />
          <circle cx="485" cy="200" r="32" fill="#0f200f" opacity="0.5" />
          <circle cx="515" cy="230" r="28" fill="#0a150a" opacity="0.4" />
          <rect x="500" y="242" width="7" height="36" fill="#0d150d" opacity="0.4" rx="3" />

          {/* Tent - proper shape */}
          <path d="M 200 110 L 260 65 L 320 110 L 330 275 L 190 275 Z" fill="#142018" opacity="0.35" />
          <path d="M 200 110 L 260 65 L 320 110" fill="#1a2a1a" opacity="0.45" stroke="#1e3520" strokeWidth="1" />
          {/* Tent peak */}
          <line x1="260" y1="65" x2="260" y2="55" stroke="#2a3a2a" strokeWidth="2" />
          {/* Tent opening */}
          <path d="M 240 275 Q 260 220 280 275" fill="#0d150d" opacity="0.3" />

          {/* Table - detailed with cloth */}
          <rect x="150" y="230" width="140" height="4" rx="2" fill="rgba(255,255,255,0.07)" />
          <path d="M 148 234 L 155 275 L 160 275 L 155 234" fill="rgba(255,255,255,0.04)" />
          <path d="M 292 234 L 285 275 L 280 275 L 285 234" fill="rgba(255,255,255,0.04)" />
          {/* Table cloth drape */}
          <path d="M 148 230 Q 220 238 292 230" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
          {/* Food items on table */}
          <circle cx="170" cy="227" r="6" fill="rgba(255,200,80,0.12)" />
          <circle cx="190" cy="228" r="4" fill="rgba(200,100,100,0.1)" />
          <rect x="208" y="224" width="10" height="7" rx="2" fill="rgba(180,160,120,0.1)" />
          <circle cx="235" cy="227" r="5" fill="rgba(255,220,100,0.1)" />
          <rect x="252" y="225" width="8" height="6" rx="1" fill="rgba(200,180,140,0.1)" />
          <circle cx="275" cy="228" r="4.5" fill="rgba(255,180,100,0.12)" />

          <Buntings y={48} />
          <StringLights count={16} y={75} />
          <StringLights count={12} y={100} />
          <PeopleSilhouettes />
        </svg>
      </motion.div>

      {/* Warm ambient glow */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{ opacity: phase >= 2 ? 0.7 : 0 }}
        transition={{ duration: 2.5 }}
        style={{ background: 'radial-gradient(ellipse 60% 40% at 50% 75%, rgba(255,170,40,0.06) 0%, transparent 70%)' }}
      />
    </div>
  );
}

/* ═══════════════ CINEMATIC INTRO ═══════════════ */
function IntroSplash({ onComplete }) {
  const canvasRef = useRef(null);
  const textOverlayRef = useRef(null);
  const requestRef = useRef(null);
  const startTimeRef = useRef(null);
  const doneRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const textOverlay = textOverlayRef.current;
    if (!canvas || !textOverlay) return;
    const heading = textOverlay.querySelector('h1');
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx || !heading) return;
    const W = window.innerWidth;
    const H = window.innerHeight;
    canvas.width = W;
    canvas.height = H;

    // Sample text pixels
    const offscreen = document.createElement('canvas');
    offscreen.width = W;
    offscreen.height = H;
    const offCtx = offscreen.getContext('2d');
    const fontSize = Math.min(W * 0.18, 180);
    offCtx.font = `900 ${fontSize}px Milker, Outfit, sans-serif`;
    offCtx.textAlign = 'center';
    offCtx.textBaseline = 'middle';
    offCtx.fillStyle = '#fff';
    offCtx.fillText('GalaGo', W / 2, H / 2 - 10);

    const tagSize = Math.min(W * 0.015, 14);
    offCtx.font = `700 ${tagSize}px League Spartan, Outfit, sans-serif`;
    offCtx.letterSpacing = '6px';
    offCtx.fillText('EVENTS ON AUTOPILOT', W / 2, H / 2 + fontSize * 0.45);

    const imageData = offCtx.getImageData(0, 0, W, H).data;
    const textPoints = [];
    const step = 4;
    for (let y = 0; y < H; y += step) {
      for (let x = 0; x < W; x += step) {
        const idx = (y * W + x) * 4;
        if (imageData[idx + 3] > 128) {
          textPoints.push({ x, y });
        }
      }
    }

    const PARTICLE_COUNT = Math.min(textPoints.length, 600);
    const colors = ['#FF6B6B', '#FFD93D', '#6C63FF', '#00C9A7', '#FF8E53', '#A78BFA', '#F472B6', '#FBBF24', '#ffffff'];
    const selectedPoints = [];
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      selectedPoints.push(textPoints[Math.floor((i / PARTICLE_COUNT) * textPoints.length)]);
    }

    const particles = selectedPoints.map((target, i) => {
      const angle = Math.random() * Math.PI * 2;
      const dist = Math.max(W, H) * 0.9 + Math.random() * 500;
      const startX = W / 2 + Math.cos(angle) * dist;
      const startY = H / 2 + Math.sin(angle) * dist;
      const swirlAngle = angle + Math.PI * 0.6 + (Math.random() - 0.5) * 1.5;
      const swirlDist = 150 + Math.random() * 250;
      const midX = W / 2 + Math.cos(swirlAngle) * swirlDist;
      const midY = H / 2 + Math.sin(swirlAngle) * swirlDist;
      return {
        startX, startY, midX, midY,
        targetX: target.x, targetY: target.y,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 3 + Math.random() * 5,
        rotation: Math.random() * 360,
        rotSpeed: (Math.random() - 0.5) * 10,
        shape: Math.random() > 0.5 ? 'rect' : Math.random() > 0.5 ? 'circle' : 'star',
        delay: Math.random() * 0.08,
      };
    });

    // Timeline: swirl → form → crossfade → hold → exit
    const ANIM_DURATION = 2200;   // particle animation
    const CROSSFADE_MS = 600;     // smooth blend duration
    const HOLD_MS = 1000;         // hold styled text
    const SWIRL_END = 0.5;
    const FORM_END = 0.82;

    function easeInOutQuad(t) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; }
    function lerp(a, b, t) { return a + (b - a) * t; }

    function drawStar(ctx2, cx, cy, r, rot) {
      ctx2.save();
      ctx2.translate(cx, cy);
      ctx2.rotate((rot * Math.PI) / 180);
      ctx2.beginPath();
      for (let i = 0; i < 5; i++) {
        const a = (i * 4 * Math.PI) / 5 - Math.PI / 2;
        const method = i === 0 ? 'moveTo' : 'lineTo';
        ctx2[method](Math.cos(a) * r, Math.sin(a) * r);
      }
      ctx2.closePath();
      ctx2.fill();
      ctx2.restore();
    }

    function animate(timestamp) {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;

      // Phase timing
      const animProgress = Math.min(elapsed / ANIM_DURATION, 1);
      const crossfadeElapsed = Math.max(0, elapsed - ANIM_DURATION * FORM_END);
      const crossfadeProgress = Math.min(crossfadeElapsed / CROSSFADE_MS, 1);
      // Smooth eased crossfade
      const crossfadeEased = crossfadeProgress < 0.5
        ? 2 * crossfadeProgress * crossfadeProgress
        : 1 - Math.pow(-2 * crossfadeProgress + 2, 2) / 2;

      // Drive overlay opacity frame-by-frame via refs (no state flips)
      textOverlay.style.opacity = crossfadeEased;
      canvas.style.opacity = 1 - crossfadeEased;

      // Scale-up + glow on the text
      const scale = 0.92 + 0.08 * crossfadeEased;
      const glowIntensity = crossfadeEased;
      heading.style.transform = `scale(${scale})`;
      heading.style.filter =
        `drop-shadow(0 0 ${40 * glowIntensity}px rgba(255,107,107,${0.4 * glowIntensity})) drop-shadow(0 0 ${80 * glowIntensity}px rgba(108,99,255,${0.25 * glowIntensity}))`;

      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = '#0a0a0a';
      ctx.fillRect(0, 0, W, H);

      for (const p of particles) {
        const pProgress = Math.max(0, Math.min(1, (animProgress - p.delay) / (1 - p.delay)));
        let px, py, alpha, size;

        if (pProgress < SWIRL_END) {
          const t = easeInOutQuad(pProgress / SWIRL_END);
          px = lerp(p.startX, p.midX, t);
          py = lerp(p.startY, p.midY, t);
          alpha = 0.3 + t * 0.6;
          size = p.size * (1.2 - t * 0.3);
        } else if (pProgress < FORM_END) {
          const t = easeInOutQuad((pProgress - SWIRL_END) / (FORM_END - SWIRL_END));
          px = lerp(p.midX, p.targetX, t);
          py = lerp(p.midY, p.targetY, t);
          alpha = 0.9;
          size = lerp(p.size * 0.9, 2.8, t);
        } else {
          px = p.targetX;
          py = p.targetY;
          alpha = 1;
          size = 2.5;
        }

        const rot = p.rotation + p.rotSpeed * elapsed * 0.05;
        ctx.globalAlpha = alpha;
        ctx.fillStyle = pProgress > FORM_END * 0.95 ? '#ffffff' : p.color;

        if (p.shape === 'circle') {
          ctx.beginPath();
          ctx.arc(px, py, size / 2, 0, Math.PI * 2);
          ctx.fill();
        } else if (p.shape === 'rect') {
          ctx.save();
          ctx.translate(px, py);
          ctx.rotate((rot * Math.PI) / 180);
          ctx.fillRect(-size / 2, -size / 2, size, size);
          ctx.restore();
        } else {
          drawStar(ctx, px, py, size / 2, rot);
        }
      }
      ctx.globalAlpha = 1;

      // Check if we're done: crossfade complete + hold finished
      const totalNeeded = ANIM_DURATION * FORM_END + CROSSFADE_MS + HOLD_MS;
      if (elapsed >= totalNeeded && !doneRef.current) {
        doneRef.current = true;
        onComplete();
        return;
      }

      requestRef.current = requestAnimationFrame(animate);
    }

    requestRef.current = requestAnimationFrame(animate);
    return () => { if (requestRef.current) cancelAnimationFrame(requestRef.current); };
  }, [onComplete]);

  return (
    <motion.div
      className="fixed inset-0 z-[200] bg-[#0a0a0a]"
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <canvas ref={canvasRef} className="w-full h-full" style={{ opacity: 1 }} />

      {/* Styled HTML text — driven by animation loop, not CSS transitions */}
      <div
        ref={textOverlayRef}
        className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
        style={{ opacity: 0 }}
      >
        <h1
          className="font-black leading-none"
          style={{
            fontSize: 'min(18vw, 11.25rem)',
            fontFamily: "'Milker', 'Outfit', sans-serif",
            background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 20%, #FFD93D 40%, #00C9A7 60%, #6C63FF 80%, #A78BFA 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            marginTop: '-10px',
            transform: 'scale(0.92)',
          }}
        >
          GalaGo
        </h1>
        <p
          className="font-bold uppercase tracking-[0.3em] mt-3"
          style={{
            fontSize: 'min(3vw, 1.875rem)',
            fontFamily: "'League Spartan', 'Outfit', sans-serif",
            color: 'rgba(255,255,255,0.5)',
          }}
        >
          EVENTS ON AUTOPILOT
        </p>
      </div>
    </motion.div>
  );
}

/* ═══════════════ CLICK CONFETTI / FIREWORKS ═══════════════ */
function ClickConfetti() {
  const [bursts, setBursts] = useState([]);

  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px) and (pointer: fine)');
    if (!media.matches) return undefined;

    const handleClick = (e) => {
      const id = Date.now();
      const particles = Array.from({ length: 18 }).map((_, i) => {
        const angle = (i / 18) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
        const velocity = 60 + Math.random() * 80;
        const colors = ['#FF6B6B', '#FFD93D', '#6C63FF', '#00C9A7', '#FF8E53', '#A78BFA', '#F472B6', '#FBBF24'];
        const shapes = ['circle', 'star', 'rect'];
        return {
          x: 0, y: 0,
          vx: Math.cos(angle) * velocity,
          vy: Math.sin(angle) * velocity - 30,
          color: colors[Math.floor(Math.random() * colors.length)],
          size: 3 + Math.random() * 5,
          shape: shapes[Math.floor(Math.random() * shapes.length)],
          rotation: Math.random() * 360,
          rotSpeed: (Math.random() - 0.5) * 15,
          duration: 0.8 + Math.random() * 0.4,
        };
      });
      setBursts(prev => [...prev, { id, x: e.clientX, y: e.clientY, particles }]);
      setTimeout(() => setBursts(prev => prev.filter(b => b.id !== id)), 1200);
    };
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none z-[95] overflow-hidden hidden lg:block">
      {bursts.map(burst => (
        <div key={burst.id} className="absolute" style={{ left: burst.x, top: burst.y }}>
          {burst.particles.map((p, i) => (
            <motion.div
              key={i}
              initial={{ x: 0, y: 0, opacity: 1, scale: 1, rotate: p.rotation }}
              animate={{
                x: p.vx,
                y: p.vy + 120,
                opacity: 0,
                scale: 0.2,
                rotate: p.rotation + p.rotSpeed * 60,
              }}
              transition={{ duration: p.duration, ease: [0.22, 1, 0.36, 1] }}
              className="absolute"
              style={{ width: p.size, height: p.size }}
            >
              {p.shape === 'circle' && (
                <div className="w-full h-full rounded-full" style={{ backgroundColor: p.color }} />
              )}
              {p.shape === 'star' && (
                <svg viewBox="0 0 10 10" className="w-full h-full">
                  <polygon points="5,0 6.2,3.5 10,3.5 7,5.8 8,10 5,7.5 2,10 3,5.8 0,3.5 3.8,3.5" fill={p.color} />
                </svg>
              )}
              {p.shape === 'rect' && (
                <div className="w-full h-full rounded-sm" style={{ backgroundColor: p.color }} />
              )}
            </motion.div>
          ))}
        </div>
      ))}
    </div>
  );
}

/* ═══════════════ TEXT SCRAMBLE HOOK ═══════════════ */
const POP_DIRECTIONS = {
  bottom: { y: 60, x: 0, rotate: 0 },
  top: { y: -60, x: 0, rotate: 0 },
  left: { y: 0, x: -80, rotate: -5 },
  right: { y: 0, x: 80, rotate: 5 },
  scale: { y: 0, x: 0, rotate: 0, scale: 0.5 },
};

function useTextScramble(text, trigger, duration = 1500) {
  const [display, setDisplay] = useState('');
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz!@#$%^&*';

  useEffect(() => {
    if (!trigger) return;
    let frame = 0;
    const totalFrames = duration / 30;
    const interval = setInterval(() => {
      frame++;
      const progress = frame / totalFrames;
      const revealed = Math.floor(progress * text.length);
      let result = '';
      for (let i = 0; i < text.length; i++) {
        if (text[i] === ' ') { result += ' '; continue; }
        result += i < revealed ? text[i] : chars[Math.floor(Math.random() * chars.length)];
      }
      setDisplay(result);
      if (frame >= totalFrames) { setDisplay(text); clearInterval(interval); }
    }, 30);
    return () => clearInterval(interval);
  }, [trigger, text, duration]);

  return display;
}

/* ═══════════════ REVEAL WRAPPER ═══════════════ */
const Pop = memo(function Pop({ children, delay = 0, from = 'bottom', className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  const d = POP_DIRECTIONS[from] || POP_DIRECTIONS.bottom;
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, ...d }}
      animate={inView ? { opacity: 1, y: 0, x: 0, rotate: 0, scale: 1 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
});

/* ═══════════════ 3D TILT CARD ═══════════════ */
const TiltCard = memo(function TiltCard({ children, className = '' }) {
  const ref = useRef(null);
  const boundsRef = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [8, -8]), { stiffness: 200, damping: 20 });
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-8, 8]), { stiffness: 200, damping: 20 });

  const handleEnter = () => {
    boundsRef.current = ref.current?.getBoundingClientRect() ?? null;
  };

  const handleMouse = (e) => {
    const rect = boundsRef.current;
    if (!rect) return;
    x.set((e.clientX - rect.left) / rect.width - 0.5);
    y.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const handleLeave = () => {
    boundsRef.current = null;
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseEnter={handleEnter}
      onMouseMove={handleMouse}
      onMouseLeave={handleLeave}
      style={{ rotateX, rotateY, transformPerspective: 800 }}
      className={className}
    >
      {children}
    </motion.div>
  );
});

/* ═══════════════ DATA ═══════════════ */
const powers = [
  { icon: Clock, label: 'Schedules', color: '#FF6B6B', desc: 'Auto-synced timelines pushed to every attendee in real-time.' },
  { icon: Users, label: 'People Ops', color: '#6C63FF', desc: 'RSVPs, arrivals, dietary prefs, plus-ones — sorted automatically.' },
  { icon: MapPin, label: 'Logistics', color: '#00C9A7', desc: 'Rooms, transport, venues mapped and assigned. No spreadsheets.' },
  { icon: Ticket, label: 'AI Tickets', color: '#FFD93D', desc: 'Snap a travel ticket. Our AI reads it. Boom — details captured.' },
  { icon: Camera, label: 'Memories', color: '#FF6B6B', desc: 'Shared album for everyone. Upload, tag, relive the magic.' },
  { icon: Gift, label: 'Wishlists', color: '#6C63FF', desc: 'Curated gift suggestions so nobody shows up with another toaster.' },
];

const vibes = [
  { emoji: '🎪', who: 'Planners', pitch: 'Your digital command center. Delegate tasks, track progress, and breathe.', gradient: 'from-[#FF6B6B] to-[#FF8E53]' },
  { emoji: '🥂', who: 'Hosts', pitch: 'Stop coordinating. Start celebrating. We keep the gears turning invisibly.', gradient: 'from-[#6C63FF] to-[#A78BFA]' },
  { emoji: '🎉', who: 'Attendees', pitch: 'One link. All info. Itinerary, maps, dress code, photos — everything.', gradient: 'from-[#00C9A7] to-[#34D399]' },
];

/* ═══════════════ MAIN COMPONENT ═══════════════ */
export default function LandingPageV3() {
  const [introComplete, setIntroComplete] = useState(false);
  const [navSolid, setNavSolid] = useState(false);
  const [fastHero, setFastHero] = useState(false);

  const containerRef = useRef(null);

  const scrambleRef = useRef(null);
  const scrambleInView = useInView(scrambleRef, { once: true });
  const scrambled = useTextScramble('We handle the chaos. You handle the champagne.', scrambleInView, 2000);

  useEffect(() => {
    const fn = () => {
      const next = window.scrollY > 80;
      setNavSolid(prev => (prev === next ? prev : next));
    };
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const onIntroComplete = useMemo(() => () => setIntroComplete(true), []);
  const heroMotionDelays = useMemo(() => (
    fastHero
      ? { container: 0.12, badge: 0.16, underline: 0.2, body: 0.18, ctas: 0.22 }
      : { container: 0.5, badge: 0.6, underline: 1.2, body: 1, ctas: 1.2 }
  ), [fastHero]);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 767px)');
    const sync = () => setFastHero(media.matches);

    sync();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', sync);
      return () => media.removeEventListener('change', sync);
    }

    media.addListener(sync);
    return () => media.removeListener(sync);
  }, []);

  /* ─── Funky cursor ─── */
  const cursorX = useMotionValue(-100);
  const cursorY = useMotionValue(-100);
  const springCursorX = useSpring(cursorX, { stiffness: 500, damping: 35 });
  const springCursorY = useSpring(cursorY, { stiffness: 500, damping: 35 });
  const trailX = useSpring(cursorX, { stiffness: 150, damping: 20 });
  const trailY = useSpring(cursorY, { stiffness: 150, damping: 20 });

  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px) and (pointer: fine)');
    if (!media.matches) return undefined;

    const move = (e) => { cursorX.set(e.clientX); cursorY.set(e.clientY); };
    window.addEventListener('mousemove', move);
    return () => window.removeEventListener('mousemove', move);
  }, [cursorX, cursorY]);

  return (
    <>
      {/* INTRO ANIMATION */}
      <AnimatePresence>
        {!introComplete && <IntroSplash onComplete={onIntroComplete} />}
      </AnimatePresence>

      <div ref={containerRef} className="min-h-screen bg-[#0d0d0d] text-white overflow-x-hidden selection:bg-[#FF6B6B]/30" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>

        {/* Click confetti burst */}
        <ClickConfetti />

        {/* Funky custom cursors (desktop only) */}
        <motion.div
          className="fixed top-0 left-0 pointer-events-none z-[90] hidden lg:flex items-center justify-center"
          style={{ x: springCursorX, y: springCursorY, translateX: '-50%', translateY: '-50%' }}
        >
          <div className="w-3 h-3 rounded-full bg-[#FF6B6B] shadow-[0_0_12px_rgba(255,107,107,0.5)]" />
        </motion.div>
        <motion.div
          className="fixed top-0 left-0 pointer-events-none z-[89] hidden lg:flex items-center justify-center"
          style={{ x: trailX, y: trailY, translateX: '-50%', translateY: '-50%' }}
        >
          <div className="w-8 h-8 rounded-full border border-[#6C63FF]/30 flex items-center justify-center">
            <Confetti className="w-3.5 h-3.5 text-[#FFD93D]/50" weight="fill" />
          </div>
        </motion.div>

        {/* ═══ NAV ═══ */}
        <motion.nav
          initial={{ y: -80 }}
          animate={{ y: introComplete ? 0 : -80 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navSolid ? 'bg-[#0d0d0d]/90 backdrop-blur-2xl border-b border-white/5' : ''}`}
        >
          <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 sm:px-10">
            <Link to="/" className="flex items-center gap-2 group">
              <GalagoLogo size={32} className="rounded-lg border border-white/20" />
              <span className="text-lg font-black tracking-tight">GALA<span className="text-[#FF6B6B]">GO</span></span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              {['Superpowers', 'Vibes', 'Origin'].map((i) => (
                <a key={i} href={`#${i.toLowerCase()}`} className="text-xs font-bold uppercase tracking-[0.2em] text-white/30 hover:text-white transition-colors">{i}</a>
              ))}
            </div>
            <Link to="/login">
              <button className="rounded-full bg-[#FF6B6B] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#FF8E53] transition-colors shadow-lg shadow-[#FF6B6B]/20">
                Jump In <ArrowUpRight className="inline h-4 w-4 ml-1" />
              </button>
            </Link>
          </div>
        </motion.nav>

        {/* ═══ HERO ═══ */}
        <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
          {/* Animated gradient blobs */}
          <motion.div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] rounded-full bg-[#FF6B6B]/10 blur-[150px]"
            animate={{ x: [0, 50, 0], y: [0, -30, 0] }} transition={{ duration: 15, repeat: Infinity }} />
          <motion.div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] rounded-full bg-[#6C63FF]/10 blur-[120px]"
            animate={{ x: [0, -40, 0], y: [0, 40, 0] }} transition={{ duration: 12, repeat: Infinity, delay: 3 }} />
          <motion.div className="absolute top-[30%] left-[40%] w-[300px] h-[300px] rounded-full bg-[#FFD93D]/5 blur-[100px]"
            animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 8, repeat: Infinity }} />

          <div className="relative z-10 mx-auto max-w-[1400px] px-6 sm:px-10 w-full">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left */}
              <motion.div
                initial={{ opacity: 0, x: -60 }}
                animate={introComplete ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: heroMotionDelays.container, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={introComplete ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: heroMotionDelays.badge }}
                  className="inline-flex items-center gap-2 rounded-full border border-[#FF6B6B]/30 bg-[#FF6B6B]/10 px-4 py-1.5 mb-8"
                >
                  <Confetti className="h-4 w-4 text-[#FF6B6B]" weight="fill" />
                  <span className="text-xs font-bold text-[#FF6B6B] uppercase tracking-wider">Event Autopilot</span>
                </motion.div>

                <h1 className="text-5xl sm:text-6xl lg:text-[5.5rem] font-bold leading-[0.95] tracking-tight" style={{ fontFamily: "'League Spartan', sans-serif" }}>
                  <span className="block">While you</span>
                  <span className="block mt-2">
                    <span className="relative inline-block">
                      <span className="relative z-10">celebrate</span>
                      <motion.span
                        className="absolute -bottom-1 left-0 right-0 h-4 bg-[#FFD93D]/30 rounded-full -z-0"
                        initial={{ scaleX: 0 }}
                        animate={introComplete ? { scaleX: 1 } : {}}
                        transition={{ delay: heroMotionDelays.underline, duration: 0.6 }}
                        style={{ originX: 0 }}
                      />
                    </span>
                    <span className="text-white/20">,</span>
                  </span>
                  <span className="block mt-2">
                    we{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B6B] via-[#FFD93D] to-[#6C63FF]">
                      coordinate.
                    </span>
                  </span>
                </h1>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={introComplete ? { opacity: 1 } : {}}
                  transition={{ delay: heroMotionDelays.body }}
                  className="mt-8 text-lg text-white/35 max-w-lg leading-relaxed"
                >
                  The wildly smart event platform that puts your entire celebration 
                  on autopilot — so you can actually enjoy it.
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={introComplete ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: heroMotionDelays.ctas }}
                  className="mt-10 flex gap-4 flex-wrap"
                >
                  <Link to="/login">
                    <button className="group flex items-center gap-2 rounded-full bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] px-8 py-4 text-base font-bold text-white shadow-xl shadow-[#FF6B6B]/20 hover:shadow-[#FF6B6B]/40 transition-all hover:scale-[1.02]">
                      Launch Your Event
                      <Rocket className="h-5 w-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" weight="fill" />
                    </button>
                  </Link>
                  <a href="#superpowers">
                    <button className="flex items-center gap-2 rounded-full border border-white/10 px-8 py-4 text-base font-bold text-white/50 hover:text-white hover:border-white/25 transition-all">
                      <Eye className="h-5 w-5" />
                      See the Magic
                    </button>
                  </a>
                </motion.div>
              </motion.div>

              {/* Right: Mascot VIDEO with funky frame */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8, rotate: 5 }}
                animate={introComplete ? { opacity: 1, scale: 1, rotate: 0 } : {}}
                transition={{ delay: 0.7, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="relative"
              >
                <TiltCard className="relative cursor-pointer">
                  {/* Neon border glow */}
                  <div className="absolute -inset-1 bg-gradient-to-r from-[#FF6B6B] via-[#6C63FF] to-[#00C9A7] rounded-[2rem] blur-sm opacity-40" />
                  <div className="relative rounded-[2rem] overflow-hidden border-2 border-white/10 h-[320px] sm:h-[400px] lg:h-[460px]">
                    <video
                      src="https://customer-assets.emergentagent.com/job_relax-events/artifacts/6128wzah_Bushbaby_Event_SaaS_Animation.mp4"
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                </TiltCard>

                {/* Floating emojis */}
                {['🍹', '🎉', '✨', '🎪', '🥳'].map((e, i) => (
                  <motion.span
                    key={i}
                    className="absolute text-3xl pointer-events-none"
                    style={{
                      top: `${15 + i * 18}%`,
                      left: i % 2 === 0 ? '-8%' : '92%',
                    }}
                    animate={{
                      y: [0, -15, 0],
                      rotate: [0, i % 2 === 0 ? 15 : -15, 0],
                      opacity: [0.6, 1, 0.6],
                    }}
                    transition={{ duration: 3 + i * 0.5, repeat: Infinity, delay: i * 0.4 }}
                  >
                    {e}
                  </motion.span>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* ═══ SCRAMBLE QUOTE ═══ */}
        <section className="py-20 sm:py-28 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0d] via-[#111119] to-[#0d0d0d]" />
          <div className="relative z-10 mx-auto max-w-5xl px-6 sm:px-10 text-center" ref={scrambleRef}>
            <p className="text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight" style={{ fontFamily: "'League Spartan', sans-serif" }}>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B6B] via-[#FFD93D] to-[#6C63FF]">
                {scrambled || '\u00A0'}
              </span>
            </p>
          </div>
        </section>

        {/* ═══ RAINBOW MARQUEE ═══ */}
        <section className="py-4 border-y border-white/20 overflow-hidden">
          <div className="marquee-mask">
            <div className="inline-flex animate-marquee-fast">
              {[...Array(8)].map((_, i) => (
                <span key={i} className="inline-flex items-center gap-6 mx-6 whitespace-nowrap">
                  {['ZERO STRESS', 'MAXIMUM VIBES', 'PURE MAGIC', 'AUTO PILOT'].map((t, j) => (
                    <React.Fragment key={j}>
                      <span className="text-2xl font-bold tracking-tight" style={{
                        color: ['#FF6B6B55', '#6C63FF55', '#00C9A755', '#FFD93D55'][j]
                      }}>{t}</span>
                      <span style={{ color: ['#FF6B6B88', '#6C63FF88', '#00C9A788', '#FFD93D88'][j] }}>&#x2726;</span>
                    </React.Fragment>
                  ))}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ SUPERPOWERS ═══ */}
        <section id="superpowers" className="py-24 sm:py-32 relative">
          <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
            <Pop>
              <div className="mb-16 max-w-2xl">
                <div className="inline-flex items-center gap-2 rounded-full border border-[#6C63FF]/30 bg-[#6C63FF]/10 px-4 py-1.5 mb-6">
                  <Lightning className="h-4 w-4 text-[#6C63FF]" weight="fill" />
                  <span className="text-xs font-bold text-[#6C63FF] uppercase tracking-wider">Superpowers</span>
                </div>
                <h2 className="text-4xl sm:text-6xl font-bold tracking-tight leading-[1.05]" style={{ fontFamily: "'League Spartan', sans-serif" }}>
                  Six reasons to ditch
                  <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B6B] to-[#6C63FF]">the spreadsheet.</span>
                </h2>
              </div>
            </Pop>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {powers.map((p, i) => (
                <Pop key={p.label} delay={i * 0.08} from={['left', 'bottom', 'right', 'left', 'bottom', 'right'][i]}>
                  <TiltCard>
                    <div className="group relative rounded-2xl border border-white/10 bg-white/[0.02] p-7 h-full hover:border-white/10 transition-all duration-500 overflow-hidden cursor-pointer">
                      {/* Corner glow */}
                      <div className="absolute -top-10 -right-10 w-24 h-24 rounded-full blur-2xl opacity-0 group-hover:opacity-30 transition-opacity duration-700" style={{ backgroundColor: p.color }} />

                      <div className="relative z-10">
                        <div className="flex items-center justify-between mb-6">
                          <div className="h-12 w-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: p.color + '15' }}>
                            <p.icon className="h-6 w-6" style={{ color: p.color }} weight="duotone" />
                          </div>
                          <ArrowUpRight className="h-5 w-5 text-white/10 group-hover:text-white/40 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                        </div>
                        <h3 className="text-xl font-bold mb-2 group-hover:text-white transition-colors">{p.label}</h3>
                        <p className="text-sm text-white/30 leading-relaxed group-hover:text-white/50 transition-colors">{p.desc}</p>
                      </div>
                    </div>
                  </TiltCard>
                </Pop>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ TRANSFORMATION SECTION — replaces empty sticky ═══ */}
        <section className="relative py-24 sm:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0d] via-[#0a0a15] to-[#0d0d0d]" />

          <div className="relative z-10 mx-auto max-w-[1400px] px-6 sm:px-10">
            {/* Section header */}
            <Pop>
              <div className="text-center mb-20">
                <h2 className="text-5xl sm:text-7xl lg:text-[6rem] font-bold tracking-tighter leading-[0.9]" style={{ fontFamily: "'League Spartan', sans-serif" }}>
                  <span className="text-white/5">Event planning</span>
                  <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B6B] via-[#FFD93D] to-[#6C63FF]">reimagined.</span>
                </h2>
                <p className="mt-6 text-white/20 text-lg max-w-xl mx-auto">
                  Not another boring tool. A living, breathing celebration engine.
                </p>
              </div>
            </Pop>

            {/* Before / After comparison */}
            <div className="grid lg:grid-cols-2 gap-6 mb-20">
              <Pop from="left">
                <div className="rounded-3xl border border-white/5 bg-white/[0.02] p-8 sm:p-10 relative overflow-hidden group">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF6B6B]/5 rounded-full blur-3xl" />
                  <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-8">
                      <div className="h-3 w-3 rounded-full bg-[#FF6B6B] animate-pulse" />
                      <span className="text-xs font-black uppercase tracking-[0.2em] text-[#FF6B6B]">Before GalaGo</span>
                    </div>
                    <div className="space-y-5">
                      {[
                        '47 WhatsApp groups for one wedding',
                        'Guests calling at 3 AM: "Which hotel?"',
                        'Excel sheets that haunt your dreams',
                        'Vendor coordination via telepathy',
                        'RSVP tracking on sticky notes',
                        'You: stressed, exhausted, crying in the bathroom',
                      ].map((item, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: i * 0.08 }}
                          className="flex items-start gap-3"
                        >
                          <span className="text-[#FF6B6B]/60 mt-0.5 text-lg">&#x2717;</span>
                          <span className="text-white/30 text-sm leading-relaxed">{item}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </Pop>

              <Pop from="right">
                <div className="rounded-3xl border border-[#00C9A7]/10 bg-gradient-to-br from-[#00C9A7]/[0.04] to-transparent p-8 sm:p-10 relative overflow-hidden group">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[#00C9A7]/5 rounded-full blur-3xl" />
                  <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-8">
                      <div className="h-3 w-3 rounded-full bg-[#00C9A7]" />
                      <span className="text-xs font-black uppercase tracking-[0.2em] text-[#00C9A7]">With GalaGo</span>
                    </div>
                    <div className="space-y-5">
                      {[
                        'One portal. Every guest sees everything.',
                        'Smart directions + room assignments auto-sent',
                        'AI reads travel tickets in seconds',
                        'Vendor timelines synced to your itinerary',
                        'Real-time RSVP dashboard with plus-ones',
                        'You: cocktail in hand, living your best life',
                      ].map((item, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, x: 20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: i * 0.08 }}
                          className="flex items-start gap-3"
                        >
                          <span className="text-[#00C9A7] mt-0.5 text-lg">&#x2713;</span>
                          <span className="text-white/50 text-sm leading-relaxed">{item}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </Pop>
            </div>

            {/* Horizontal scrolling feature pills */}
            <Pop>
              <div className="relative">
                <p className="text-center text-xs font-black uppercase tracking-[0.3em] text-white/15 mb-8">Scroll through capabilities</p>
                <div className="overflow-x-auto pb-4 hide-scrollbar">
                  <div className="flex gap-4 min-w-max px-4">
                    {[
                      { label: 'Itinerary Builder', icon: Clock, color: '#FF6B6B' },
                      { label: 'Guest Tracker', icon: Users, color: '#6C63FF' },
                      { label: 'Room Mapper', icon: MapPin, color: '#00C9A7' },
                      { label: 'Ticket Scanner', icon: Ticket, color: '#FFD93D' },
                      { label: 'Photo Gallery', icon: Camera, color: '#FF6B6B' },
                      { label: 'Gift Registry', icon: Gift, color: '#6C63FF' },
                      { label: 'Live Updates', icon: Megaphone, color: '#00C9A7' },
                      { label: 'Vendor Sync', icon: Headset, color: '#FFD93D' },
                    ].map((pill, i) => (
                      <motion.div
                        key={pill.label}
                        whileHover={{ scale: 1.05, y: -4 }}
                        className="flex items-center gap-3 rounded-full border border-white/5 bg-white/[0.03] px-6 py-3.5 cursor-pointer hover:bg-white/[0.06] transition-colors"
                      >
                        <pill.icon className="h-5 w-5 flex-shrink-0" style={{ color: pill.color }} weight="duotone" />
                        <span className="text-sm font-bold text-white/50 whitespace-nowrap">{pill.label}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </Pop>

            {/* Galago wisdom quote */}
            <Pop delay={0.2}>
              <div className="mt-20 text-center">
                <div className="inline-block rounded-2xl border border-white/5 bg-white/[0.02] px-10 py-8 max-w-2xl">
                  <span className="text-4xl block mb-4">🦊</span>
                  <p className="text-xl sm:text-2xl text-white/50 italic leading-relaxed" style={{ fontFamily: "'League Spartan', sans-serif" }}>
                    "The best events aren't the most planned ones — they're the ones where the
                    planner actually got to <span className="text-[#FFD93D]">enjoy them</span>."
                  </p>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/15 mt-4">— The GalaGo Philosophy</p>
                </div>
              </div>
            </Pop>
          </div>
        </section>

        {/* ═══ VIBES (Who it's for) ═══ */}
        <section id="vibes" className="py-24 sm:py-32 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0d] via-[#0f0f1a] to-[#0d0d0d]" />
          <div className="relative z-10 mx-auto max-w-[1400px] px-6 sm:px-10">
            <Pop>
              <div className="text-center mb-16">
                <div className="inline-flex items-center gap-2 rounded-full border border-[#00C9A7]/30 bg-[#00C9A7]/10 px-4 py-1.5 mb-6">
                  <Heart className="h-4 w-4 text-[#00C9A7]" weight="fill" />
                  <span className="text-xs font-bold text-[#00C9A7] uppercase tracking-wider">Vibes Check</span>
                </div>
                <h2 className="text-4xl sm:text-6xl font-bold tracking-tight" style={{ fontFamily: "'League Spartan', sans-serif" }}>
                  Built for <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00C9A7] to-[#34D399]">everyone</span> at the party.
                </h2>
              </div>
            </Pop>

            <div className="grid md:grid-cols-3 gap-6">
              {vibes.map((v, i) => (
                <Pop key={v.who} delay={i * 0.15} from="scale">
                  <TiltCard>
                    <div className="group relative rounded-3xl overflow-hidden cursor-pointer h-full">
                      {/* Gradient bg */}
                      <div className={`absolute inset-0 bg-gradient-to-br ${v.gradient} opacity-[0.07] group-hover:opacity-[0.15] transition-opacity duration-700`} />
                      <div className="relative border border-white/5 group-hover:border-white/10 rounded-3xl p-8 sm:p-10 transition-all h-full">
                        <span className="text-6xl block mb-6">{v.emoji}</span>
                        <h3 className="text-2xl font-bold mb-3">{v.who}</h3>
                        <p className="text-white/35 leading-relaxed group-hover:text-white/55 transition-colors">{v.pitch}</p>
                        <Link to="/login" className="mt-6 inline-flex items-center gap-2 text-sm font-bold opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all" style={{ color: v.gradient.includes('FF6B') ? '#FF6B6B' : v.gradient.includes('6C63') ? '#6C63FF' : '#00C9A7' }}>
                          Enter as {v.who.toLowerCase()}
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                  </TiltCard>
                </Pop>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ NUMBERS EXPLOSION ═══ */}
        <section className="py-20 border-y border-white/5 relative overflow-hidden">
          <div className="absolute inset-0">
            <motion.div className="absolute inset-0 bg-gradient-to-r from-[#FF6B6B]/5 via-[#6C63FF]/5 to-[#00C9A7]/5"
              animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
              transition={{ duration: 10, repeat: Infinity }}
              style={{ backgroundSize: '200% 200%' }}
            />
          </div>
          <div className="relative z-10 mx-auto max-w-[1400px] px-6 sm:px-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { num: '600+', tag: 'Happy Humans', color: '#FF6B6B', icon: Smiley },
                { num: '100%', tag: 'Vibe Rate', color: '#FFD93D', icon: Sparkle },
                { num: '35%', tag: 'Less Panic', color: '#6C63FF', icon: Champagne },
                { num: '∞', tag: 'Memories Made', color: '#00C9A7', icon: MusicNotes },
              ].map((s, i) => (
                <Pop key={s.tag} delay={i * 0.1} from="scale">
                  <div className="text-center group cursor-pointer">
                    <s.icon className="h-8 w-8 mx-auto mb-3 group-hover:scale-125 transition-transform" style={{ color: s.color }} weight="fill" />
                    <p className="text-4xl sm:text-5xl font-bold" style={{ color: s.color }}>{s.num}</p>
                    <p className="text-xs uppercase tracking-[0.2em] text-white/25 mt-2 font-bold">{s.tag}</p>
                  </div>
                </Pop>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ ORIGIN STORY ═══ */}
        <section id="origin" className="py-24 sm:py-32">
          <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <Pop from="left">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-[#FFD93D]/30 bg-[#FFD93D]/10 px-4 py-1.5 mb-6">
                    <Megaphone className="h-4 w-4 text-[#FFD93D]" weight="fill" />
                    <span className="text-xs font-bold text-[#FFD93D] uppercase tracking-wider">Origin Story</span>
                  </div>
                  <h2 className="text-4xl sm:text-5xl font-bold tracking-tight mb-6 leading-tight" style={{ fontFamily: "'League Spartan', sans-serif" }}>
                    Four friends.
                    <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FFD93D] to-[#FF6B6B]">One wild idea.</span>
                  </h2>
                  <p className="text-white/35 text-lg leading-relaxed mb-6">
                    We threw a 200-person destination wedding and nearly lost our minds. 
                    Spreadsheets everywhere. Guests calling at 3 AM asking "which hotel am I at?"
                  </p>
                  <p className="text-white/35 text-lg leading-relaxed">
                    So we built the tool we wished existed — a single portal where every human 
                    at the event knows exactly what's happening, when, and where. 
                    No calls. No confusion. Just pure celebration energy.
                  </p>
                </div>
              </Pop>

              <Pop from="right">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Obsessive about details', icon: Eye, color: '#FF6B6B' },
                    { label: 'Powered by AI + empathy', icon: Heart, color: '#6C63FF' },
                    { label: 'Tested on real chaos', icon: Confetti, color: '#FFD93D' },
                    { label: 'Always shipping updates', icon: Rocket, color: '#00C9A7' },
                  ].map((v, i) => (
                    <TiltCard key={v.label}>
                      <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-6 hover:bg-white/[0.04] transition-all cursor-pointer group">
                        <v.icon className="h-7 w-7 mb-4 group-hover:scale-110 transition-transform" style={{ color: v.color }} weight="duotone" />
                        <p className="text-sm font-bold text-white/60 group-hover:text-white/80 transition-colors">{v.label}</p>
                      </div>
                    </TiltCard>
                  ))}
                </div>
              </Pop>
            </div>
          </div>
        </section>

        {/* ═══ BIG CTA ═══ */}
        <section className="py-24 sm:py-32 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B6B]/10 via-[#6C63FF]/5 to-[#00C9A7]/10" />
          <motion.div className="absolute top-0 left-0 w-full h-full"
            animate={{ backgroundPosition: ['0% 0%', '100% 100%'] }}
            transition={{ duration: 20, repeat: Infinity, repeatType: 'reverse' }}
            style={{
              backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(108,99,255,0.08) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255,107,107,0.08) 0%, transparent 50%)',
              backgroundSize: '200% 200%'
            }}
          />

          <div className="relative z-10 mx-auto max-w-4xl px-6 sm:px-10 text-center">
            <Pop from="scale">
              <div>
                <HandWaving className="h-16 w-16 text-[#FFD93D] mx-auto mb-6" weight="fill" />
                <h2 className="text-5xl sm:text-7xl lg:text-8xl font-bold tracking-tight leading-[0.95]" style={{ fontFamily: "'League Spartan', sans-serif" }}>
                  Your next party
                  <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B6B] via-[#FFD93D] to-[#6C63FF]">starts here.</span>
                </h2>
                <p className="mt-8 text-white/30 text-lg max-w-lg mx-auto">
                  Stop overthinking. Start celebrating. The Galago way.
                </p>
                <div className="mt-10 flex gap-4 justify-center flex-wrap">
                  <Link to="/login">
                    <button className="group flex items-center gap-3 rounded-full bg-gradient-to-r from-[#FF6B6B] via-[#FF8E53] to-[#FFD93D] px-10 py-5 text-lg font-black text-white shadow-2xl shadow-[#FF6B6B]/20 hover:shadow-[#FF6B6B]/40 transition-all hover:scale-[1.03]">
                      Let's Go
                      <Rocket className="h-6 w-6 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" weight="fill" />
                    </button>
                  </Link>
                </div>
              </div>
            </Pop>
          </div>
        </section>

        {/* ═══ CONNECT ═══ */}
        <section className="py-16 border-t border-white/5">
          <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'WhatsApp', val: 'Ping us', href: 'https://wa.me/919876543210', color: '#00C9A7' },
                { label: 'Instagram', val: '@galago.official', href: 'https://instagram.com/galago.official', color: '#FF6B6B' },
                { label: 'Email', val: 'hello@galago.in', href: 'mailto:hello@galago.in', color: '#6C63FF' },
                { label: 'Platform', val: 'Jump right in', href: '/login', internal: true, color: '#FFD93D' },
              ].map((c) => (
                <Pop key={c.label}>
                  {c.internal ? (
                    <Link to={c.href} className="block rounded-2xl border border-white/5 bg-white/[0.02] p-5 hover:bg-white/[0.05] transition-all group">
                      <p className="text-[0.625rem] uppercase tracking-[0.2em] font-bold" style={{ color: c.color + '80' }}>{c.label}</p>
                      <p className="text-sm font-bold text-white/60 mt-1.5 group-hover:text-white transition-colors">{c.val}</p>
                    </Link>
                  ) : (
                    <a href={c.href} target="_blank" rel="noreferrer" className="block rounded-2xl border border-white/5 bg-white/[0.02] p-5 hover:bg-white/[0.05] transition-all group">
                      <p className="text-[0.625rem] uppercase tracking-[0.2em] font-bold" style={{ color: c.color + '80' }}>{c.label}</p>
                      <p className="text-sm font-bold text-white/60 mt-1.5 group-hover:text-white transition-colors">{c.val}</p>
                    </a>
                  )}
                </Pop>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ FOOTER ═══ */}
        <footer className="py-8 border-t border-white/5">
          <div className="mx-auto flex max-w-[1400px] flex-col items-center justify-between gap-4 px-6 sm:flex-row sm:px-10">
            <div className="flex items-center gap-2">
              <GalagoLogo size={20} className="rounded-md border border-white/15" />
              <span className="text-sm font-bold text-white/30">GALA<span className="text-[#FF6B6B]/50">GO</span></span>
            </div>
            <div className="flex items-center gap-4 text-xs text-white/15">
              <p>&copy; 2026 GalaGo. Celebration technology.</p>
              <Link to="/privacy-policy" className="text-white/35 hover:text-white transition-colors">
                Privacy Policy
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
